import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser, PenLine, Type } from 'lucide-react';

const INK = '#0a1f2e';
const SCRIPT_FONT = 'Dancing Script';
const MAX_EXPORT_WIDTH = 1200;

/** Loads the script font used for typed signatures (once). Falls back to the browser's cursive font. */
let fontPromise = null;
const loadScriptFont = () => {
  if (fontPromise) return fontPromise;
  const timeout = (ms) => new Promise((r) => setTimeout(r, ms));
  fontPromise = (async () => {
    try {
      let link = document.getElementById('sig-script-font');
      if (!link) {
        link = document.createElement('link');
        link.id = 'sig-script-font';
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${SCRIPT_FONT.replace(' ', '+')}:wght@600&display=swap`;
        document.head.appendChild(link);
      }
      // The @font-face rule exists only once the stylesheet has loaded; before that fonts.load() finds nothing.
      if (!link.sheet) {
        await Promise.race([new Promise((r) => { link.addEventListener('load', r); link.addEventListener('error', r); }), timeout(3000)]);
      }
      if (document.fonts && document.fonts.load) await Promise.race([document.fonts.load(`600 64px "${SCRIPT_FONT}"`), timeout(3000)]);
    } catch (e) { /* falls back to the browser's cursive font */ }
  })();
  return fontPromise;
};

/** The typed name drawn in a script font, as a PNG data URL (transparent background). */
export const typedSignature = async (name) => {
  const text = (name || '').trim();
  if (!text) return null;
  await loadScriptFont();
  const c = document.createElement('canvas');
  c.width = MAX_EXPORT_WIDTH; c.height = 300;
  const ctx = c.getContext('2d');
  let size = 132;
  const font = (s) => `600 ${s}px "${SCRIPT_FONT}", "Segoe Script", "Brush Script MT", "Lucida Handwriting", cursive`;
  ctx.font = font(size);
  while (ctx.measureText(text).width > c.width - 80 && size > 36) { size -= 6; ctx.font = font(size); }
  ctx.fillStyle = INK;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(text, c.width / 2, c.height / 2 + 8);
  return c.toDataURL('image/png');
};

/** A PNG of what was drawn, at most MAX_EXPORT_WIDTH wide (keeps it well under the 200 KB limit). */
const exportCanvas = (canvas) => {
  if (canvas.width <= MAX_EXPORT_WIDTH) return canvas.toDataURL('image/png');
  const scale = MAX_EXPORT_WIDTH / canvas.width;
  const out = document.createElement('canvas');
  out.width = MAX_EXPORT_WIDTH; out.height = Math.round(canvas.height * scale);
  out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
};

/**
 * Electronic signature: draw with a mouse, finger or pen, or type it. Calls onChange({ image, method }) where image
 * is a PNG data URL (or null while empty) and method is 'drawn' or 'typed'. `name` is the signer's typed full name.
 */
const SignaturePad = ({ name, value, onChange }) => {
  const [mode, setMode] = useState(value?.method === 'typed' ? 'typed' : 'drawn');
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawing = useRef(null);
  const inkRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // High-DPI canvas sized to its box. Resizing clears it (the signer signs again).
  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const w = wrap.clientWidth;
    const h = wrap.clientHeight;
    if (canvas.width === Math.round(w * dpr) && canvas.height === Math.round(h * dpr)) return;
    canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = INK; ctx.lineWidth = 2.6;
    if (inkRef.current) { inkRef.current = false; setHasInk(false); onChangeRef.current({ image: null, method: 'drawn' }); }
  }, []);

  useEffect(() => {
    if (mode !== 'drawn') return undefined;
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => fit()) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    else window.addEventListener('resize', fit);
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', fit); };
  }, [mode, fit]);

  // Typed: the name in a script font, redrawn as the name changes.
  useEffect(() => {
    if (mode !== 'typed') return undefined;
    let live = true;
    const t = setTimeout(() => {
      typedSignature(name).then((image) => { if (live) onChangeRef.current({ image, method: 'typed' }); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [mode, name]);

  const point = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    const p = point(e);
    drawing.current = { last: p, mid: p };
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.arc(p.x, p.y, 1.3, 0, Math.PI * 2); ctx.fillStyle = INK; ctx.fill();
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const events = e.nativeEvent.getCoalescedEvents ? e.nativeEvent.getCoalescedEvents() : [e.nativeEvent];
    events.forEach((ev) => {
      const p = point(ev);
      const { last, mid } = drawing.current;
      const nextMid = { x: (last.x + p.x) / 2, y: (last.y + p.y) / 2 };
      ctx.beginPath(); ctx.moveTo(mid.x, mid.y); ctx.quadraticCurveTo(last.x, last.y, nextMid.x, nextMid.y); ctx.stroke();
      drawing.current = { last: p, mid: nextMid };
    });
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = null;
    inkRef.current = true;
    setHasInk(true);
    onChange({ image: exportCanvas(canvasRef.current), method: 'drawn' });
  };
  const clear = () => {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    inkRef.current = false;
    setHasInk(false);
    onChange({ image: null, method: 'drawn' });
  };
  const switchTo = (m) => {
    if (m === mode) return;
    setMode(m); setHasInk(false); inkRef.current = false;
    onChange({ image: null, method: m });
  };

  const tab = (m, Icon, label) => (
    <button type="button" role="tab" aria-selected={mode === m} onClick={() => switchTo(m)}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ${mode === m ? 'bg-[#0a1f2e] text-white' : 'text-[#0a1f2e] hover:bg-gray-100'}`}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-full border bg-white p-1" role="tablist" aria-label="How to sign">
          {tab('drawn', PenLine, 'Draw')}
          {tab('typed', Type, 'Type instead')}
        </div>
        {mode === 'drawn' && (
          <button type="button" onClick={clear} disabled={!hasInk} className="inline-flex items-center gap-1 rounded-full border bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-40">
            <Eraser className="h-4 w-4" />Clear
          </button>
        )}
      </div>
      <div ref={wrapRef} className="relative h-40 w-full overflow-hidden rounded-lg border-2 border-dashed border-gray-300 bg-white sm:h-44">
        {mode === 'drawn' ? (
          <>
            <canvas ref={canvasRef} aria-label="Signature pad: draw your signature here"
              className="absolute inset-0 cursor-crosshair touch-none select-none"
              onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up} />
            {!hasInk && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400">Sign here with your mouse, finger or pen</span>}
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center p-3">
            {value?.image && value.method === 'typed'
              ? <img src={value.image} alt={`Signature: ${name}`} className="max-h-full max-w-full" />
              : <span className="text-sm text-gray-400">Type your full name above to create your signature</span>}
          </div>
        )}
        <div className="pointer-events-none absolute bottom-6 left-6 right-6 border-b border-gray-300" />
        <span className="pointer-events-none absolute bottom-1.5 left-6 text-[10px] uppercase tracking-wide text-gray-400">Signature</span>
      </div>
    </div>
  );
};

export default SignaturePad;
